import { AnyAction } from 'redux';
import { AppDispatch } from '../../commons/store/store';
import apiHelper from '../../api/apiHelper';
import { startToast } from '../../commons/components/toast/toastDucks';
import { FormErrors, reset } from 'redux-form';
import {
  CostDocumentFormValues,
  InvoiceDocumentFormValues,
  RebillingFormValues,
  REPAIR_COST_FORM_NAME,
  RepairCost,
  RepairCostFormValues,
  RepairCostType,
} from './type';
import { isFieldEmpty } from '../../commons/redux-form/valueValidator';
import { isMoment } from 'moment';

const OPEN_REPAIR_COST_DIALOG = 'OPEN_REPAIR_COST_DIALOG';
const CLOSE_REPAIR_COST_DIALOG = 'CLOSE_REPAIR_COST_DIALOG';
const LOAD_REPAIR_COSTS_SUCCESS = 'LOAD_REPAIR_COSTS_SUCCESS';
const UPDATE_PENDING_FILE = 'UPDATE_PENDING_FILE';
const REMOVE_PENDING_FILE = 'REMOVE_PENDING_FILE';

export type RepairCostState = {
  dialogOpen: boolean;
  repairId: string | null;
  repairCost: RepairCost | null;
  costs: RepairCost[];
  pendingQuoteFile: File | null;
  pendingOrderFile: File | null;
  pendingInvoiceFile: File | null;
};

const initialState: RepairCostState = {
  dialogOpen: false,
  repairId: null,
  repairCost: null,
  costs: [],
  pendingQuoteFile: null,
  pendingOrderFile: null,
  pendingInvoiceFile: null,
};

export default (state = initialState, action: AnyAction): RepairCostState => {
  switch (action.type) {
    case OPEN_REPAIR_COST_DIALOG:
      return {
        ...state,
        dialogOpen: true,
        repairId: action.repairId,
        repairCost: action.repairCost,
        pendingQuoteFile: null,
        pendingOrderFile: null,
        pendingInvoiceFile: null,
      };
    case CLOSE_REPAIR_COST_DIALOG:
      return {
        ...state,
        dialogOpen: false,
        pendingQuoteFile: null,
        pendingOrderFile: null,
        pendingInvoiceFile: null,
      };
    case LOAD_REPAIR_COSTS_SUCCESS:
      return { ...state, costs: action.costs };
    case UPDATE_PENDING_FILE:
      switch (action.costType) {
        case 'QUOTE':
          return { ...state, pendingQuoteFile: action.file };
        case 'ORDER':
          return { ...state, pendingOrderFile: action.file };
        case 'INVOICE':
          return { ...state, pendingInvoiceFile: action.file };
        default:
          return state;
      }
    case REMOVE_PENDING_FILE:
      switch (action.costType) {
        case 'QUOTE':
          return { ...state, pendingQuoteFile: null };
        case 'ORDER':
          return { ...state, pendingOrderFile: null };
        case 'INVOICE':
          return { ...state, pendingInvoiceFile: null };
        default:
          return { ...state, pendingQuoteFile: null, pendingOrderFile: null, pendingInvoiceFile: null };
      }
    default:
      return state;
  }
};

export const openRepairCostDialog = (repairId: string, repairCost?: RepairCost) => (dispatch: AppDispatch) => {
  dispatch({ type: OPEN_REPAIR_COST_DIALOG, repairId, repairCost });
  dispatch(reset(REPAIR_COST_FORM_NAME));
};

export const closeRepairCostDialog = () => (dispatch: AppDispatch) => {
  dispatch({ type: CLOSE_REPAIR_COST_DIALOG });
  dispatch(reset(REPAIR_COST_FORM_NAME));
};

export const updatePendingFile = (file: File | null, costType: RepairCostType) => (dispatch: AppDispatch) => {
  dispatch({ type: UPDATE_PENDING_FILE, file, costType: costType });
};

export const removePendingFile = (costType: RepairCostType) => (dispatch: AppDispatch) => {
  dispatch({ type: REMOVE_PENDING_FILE, costType: costType });
};

export const loadRepairCosts = (repairId: string) => async (dispatch: AppDispatch) => {
  const costs: RepairCost[] = await apiHelper.get(`/api/repair-costs/repair/${repairId}`);
  dispatch({ type: LOAD_REPAIR_COSTS_SUCCESS, costs });
};

type RepairCostFormErrors = {
  quote?: Partial<Record<keyof CostDocumentFormValues, string>>;
  order?: Partial<Record<keyof CostDocumentFormValues, string>>;
  invoice?: Partial<Record<keyof InvoiceDocumentFormValues, string>>;
  rebillable?: string;
  rebilling?: Partial<Record<keyof RebillingFormValues, string>>;
  comment?: string;
};

const validateCostDocumentDate = (
  costDoc: CostDocumentFormValues | null | undefined,
): Partial<Record<keyof CostDocumentFormValues, string>> | undefined => {
  if (costDoc && (!costDoc.date || !isMoment(costDoc.date))) {
    return { date: 'Champ obligatoire' };
  }
  return undefined;
};

const validateRebilling = (
  rebilling: RebillingFormValues | null | undefined,
): Partial<Record<keyof RebillingFormValues, string>> => {
  const errors: Partial<Record<keyof RebillingFormValues, string>> = {};

  if (isFieldEmpty(rebilling?.type)) {
    errors.type = "Champ obligatoire s'il s'agit d'une re-facturation";
  }
  if (rebilling?.type === 'OTHERS' && isFieldEmpty(rebilling.precision)) {
    errors.precision = 'Veuillez préciser le type de re-facturation';
  } else if (rebilling?.precision && rebilling.precision.length > 255) {
    errors.precision = 'Longueur maximale : 255 caractères';
  }
  if (rebilling?.type && !rebilling.number) {
    errors.number = 'Champ obligatoire pour compléter une re-facturation';
  }
  if (rebilling?.type && !rebilling.date) {
    errors.date = 'Champ obligatoire pour compléter une re-facturation';
  }
  return errors;
};

export const validateRepairCostForm = (formValues: RepairCostFormValues): FormErrors<RepairCostFormValues> => {
  if (!formValues) {
    return {};
  }

  const errors: RepairCostFormErrors = {};

  const quoteDateError = validateCostDocumentDate(formValues.quote);
  if (quoteDateError) {
    errors.quote = quoteDateError;
  }

  const orderDateError = validateCostDocumentDate(formValues.order);
  if (orderDateError) {
    errors.order = orderDateError;
  }

  if (formValues.invoice) {
    const invoiceErrors: Partial<Record<keyof InvoiceDocumentFormValues, string>> = {
      ...validateCostDocumentDate(formValues.invoice),
    };
    if (!formValues.invoice.status) {
      invoiceErrors.status = 'Champ obligatoire';
    }
    if (Object.keys(invoiceErrors).length > 0) {
      errors.invoice = invoiceErrors;
    }
  }

  if (!formValues.rebillable) {
    errors.rebillable = 'Champ obligatoire';
  }

  if (formValues.rebillable === 'YES') {
    const rebillingErrors = validateRebilling(formValues.rebilling);
    if (Object.keys(rebillingErrors).length > 0) {
      errors.rebilling = rebillingErrors;
    }
  }

  if (formValues.comment && formValues.comment.length > 255) {
    errors.comment = 'Longueur maximale : 255 caractères';
  }

  return errors as FormErrors<RepairCostFormValues>;
};

const toCostDocumentPayload = (doc: CostDocumentFormValues | null | undefined, withFile: boolean) => {
  if (!doc) return null;
  return {
    number: doc.number ?? null,
    date: doc.date ? doc.date.format('yyyy-MM-DD') : null,
    cost: doc.cost != null ? parseFloat(doc.cost) : null,
    withFile,
  };
};

export const submitRepairCost =
  (
    repairId: string,
    formValues: RepairCostFormValues,
    quoteFile: File | null,
    orderFile: File | null,
    invoiceFile: File | null,
  ) =>
  async (dispatch: AppDispatch) => {
    const payload = {
      quote: toCostDocumentPayload(formValues.quote, !!formValues.quote?.fileName),
      order: toCostDocumentPayload(formValues.order, !!formValues.order?.fileName),
      invoice: formValues.invoice
        ? {
            ...toCostDocumentPayload(formValues.invoice, !!formValues.invoice?.fileName),
            status: formValues.invoice.status ?? null,
          }
        : null,
      rebillable: formValues.rebillable,
      rebilling: formValues.rebilling
        ? {
            type: formValues.rebilling.type ?? null,
            precision: formValues.rebilling.precision ?? null,
            number: formValues.rebilling.number ?? null,
            date: formValues.rebilling.date ? formValues.rebilling.date.format('yyyy-MM-DD') : null,
            providerOrderNumber: formValues.rebilling.providerOrderNumber ?? null,
          }
        : null,
      comment: formValues.comment ?? null,
    };

    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));

    if (!formValues.repairCostId) {
      // creation
      if (quoteFile) formData.append('quoteFile', quoteFile);
      if (orderFile) formData.append('orderFile', orderFile);
      if (invoiceFile) formData.append('invoiceFile', invoiceFile);

      try {
        await apiHelper.postRaw(`/api/repair-costs/repair/${repairId}`, formData);
        dispatch(closeRepairCostDialog());
        dispatch(loadRepairCosts(repairId));
        dispatch(startToast({ text: 'Le coût de réparation a été ajouté avec succès.', className: 'success' }));
      } catch {
        dispatch(startToast({ text: "Erreur : le coût de réparation n'a pas pu être ajouté.", className: 'error' }));
      }
    } else {
      // update
      if (quoteFile) formData.append('newQuoteFile', quoteFile);
      if (orderFile) formData.append('newOrderFile', orderFile);
      if (invoiceFile) formData.append('newInvoiceFile', invoiceFile);

      try {
        await apiHelper.putRaw(`/api/repair-costs/${formValues.repairCostId}`, formData);
        dispatch(closeRepairCostDialog());
        dispatch(loadRepairCosts(repairId));
        dispatch(startToast({ text: 'Le coût de réparation a été mis à jour avec succès.', className: 'success' }));
      } catch {
        dispatch(
          startToast({ text: "Erreur : le coût de réparation n'a pas pu être mis à jour.", className: 'error' }),
        );
      }
    }
  };

export const deleteRepairCost = (repairId: string, repairCostId: string) => async (dispatch: AppDispatch) => {
  try {
    await apiHelper.delete(`/api/repair-costs/${repairCostId}`);
    dispatch(closeRepairCostDialog());
    dispatch(loadRepairCosts(repairId));
    dispatch(startToast({ text: 'Le coût de réparation a été supprimé avec succès.', className: 'success' }));
  } catch {
    dispatch(startToast({ text: "Erreur : le coût de réparation n'a pas pu être supprimé.", className: 'error' }));
  }
};
